import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, CalendarIcon } from "lucide-react";
import { pluralUnit } from "./unitLabel";
import { UnitTypeSelect } from "./UnitTypeSelect";
import { formatMDY, toIsoDate, isoToDate } from "./dateFormat";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_ITEM_VALUE = "__new__";

const PILLS = [
  { value: 0.25, label: "¼" },
  { value: 0.5, label: "½" },
  { value: 0.75, label: "¾" },
  { value: 1, label: "1" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const TakeInventoryPanel = ({ month, year, authHeaders, storageAreas, isOwner, onAreasChanged, items: catalogItems, onItemsChanged, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedDataRef = useRef(false);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [addingArea, setAddingArea] = useState(false);
  const [newAreaLabel, setNewAreaLabel] = useState("");
  const [mealAverages, setMealAverages] = useState(null);
  const [mealToday, setMealToday] = useState(null);
  const [mealTodayViewDate, setMealTodayViewDate] = useState(() => toIsoDate(new Date()));
  const [orgTodayDate, setOrgTodayDate] = useState(null);
  const [confirmingBeginningFor, setConfirmingBeginningFor] = useState(null);
  const AREAS = storageAreas || [];

  const [addingItem, setAddingItem] = useState(false);
  const [addItemChoice, setAddItemChoice] = useState(NEW_ITEM_VALUE);
  const [addItemNewName, setAddItemNewName] = useState("");
  const [addItemArea, setAddItemArea] = useState(AREAS[0]?.key || "pantry");
  const [addItemUnit, setAddItemUnit] = useState("bag");
  const [addingItemBusy, setAddingItemBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("freezer");
  // Only offer active, non-merged catalog items — a merged/deactivated item would 404.
  const selectableCatalogItems = (catalogItems || []).filter((it) => it.active !== false && !it.mergedIntoItemId);

  const handleAddArea = async () => {
    if (!newAreaLabel.trim()) return;
    try {
      await axios.post(`${API}/inventory/storage-areas`, { label: newAreaLabel.trim() }, { headers: authHeaders });
      toast.success("Storage area added");
      setNewAreaLabel(""); setAddingArea(false);
      onAreasChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add storage area");
    }
  };

  const fetchMealAverages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/meal-averages`, { params: { month, year }, headers: authHeaders });
      setMealAverages(res.data);
    } catch (err) {
      console.error("Error fetching meal averages:", err);
    }
  }, [month, year, authHeaders]);

  const fetchMealToday = useCallback(async (date) => {
    try {
      const res = await axios.get(`${API}/inventory/meal-today`, { params: date ? { date } : {}, headers: authHeaders });
      setMealToday(res.data);
      // First load (no date passed) — sync the picker to the org's actual local "today",
      // which may differ from the browser's local date.
      if (!date && res.data.date) { setMealTodayViewDate(res.data.date); setOrgTodayDate(res.data.date); }
    } catch (err) {
      console.error("Error fetching today's meal counts:", err);
    }
  }, [authHeaders]);

  useEffect(() => { fetchMealAverages(); fetchMealToday(); }, [fetchMealAverages, fetchMealToday]);

  const handleMealTodayDateChange = (isoDate) => {
    setMealTodayViewDate(isoDate);
    fetchMealToday(isoDate);
  };


  const fetchData = useCallback(async () => {
    if (!hasLoadedDataRef.current) setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/take-inventory`, { params: { month, year }, headers: authHeaders });
      setData(res.data);
      const nextDrafts = {};
      (res.data.items || []).forEach((item) => {
        const existing = res.data.counts?.[item.id];
        const suggestion = res.data.suggestions?.[item.id];
        nextDrafts[item.id] = {
          currentAmount: existing?.currentAmount ?? "",
          quantityToBuy: existing?.quantityToBuy ?? (suggestion?.suggestedToBuy ?? ""),
          notes: existing?.notes ?? "",
        };
      });
      setDrafts(nextDrafts);
    } catch (err) {
      console.error("Error fetching take-inventory data:", err);
    } finally {
      setLoading(false);
      hasLoadedDataRef.current = true;
    }
  }, [month, year, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const updateDraft = (itemId, field, value) => {
    setDrafts((d) => ({ ...d, [itemId]: { ...d[itemId], [field]: value } }));
  };

  const handleSave = async (item) => {
    const draft = drafts[item.id] || {};
    if (draft.currentAmount === "" || draft.currentAmount === undefined) {
      toast.error("Enter the Current Amount first");
      return;
    }
    setSavingId(item.id);
    try {
      const res = await axios.post(`${API}/inventory/counts`, {
        itemId: item.id,
        currentAmount: parseFloat(draft.currentAmount),
        quantityToBuy: draft.quantityToBuy === "" || draft.quantityToBuy === undefined ? 0 : parseFloat(draft.quantityToBuy),
        notes: draft.notes || null,
      }, { params: { month, year }, headers: authHeaders });
      toast.success("Count saved");
      // Merge in just this item's saved count + recomputed suggestion — avoid a full refetch
      // so unsaved edits on other rows in this table aren't wiped out.
      setData((prev) => ({
        ...prev,
        counts: { ...prev.counts, [item.id]: res.data.count },
        suggestions: { ...prev.suggestions, [item.id]: res.data.suggestion },
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save count");
    } finally {
      setSavingId(null);
    }
  };

  // "Catches what the scan missed" — adds an item straight onto this month's count without
  // needing to re-run extraction. Pick an existing catalog item, or create a brand-new one
  // (same catalog Master Item List uses).
  const handleAddItem = async () => {
    const isNew = addItemChoice === NEW_ITEM_VALUE;
    if (isNew && !addItemNewName.trim()) {
      toast.error("Enter a name for the new item, or pick an existing one");
      return;
    }
    setAddingItemBusy(true);
    try {
      const payload = isNew
        ? { canonicalName: addItemNewName.trim(), storageArea: addItemArea, unitType: addItemUnit }
        : { itemId: addItemChoice };
      await axios.post(`${API}/inventory/take-inventory/add-item`, payload,
        { params: { month, year }, headers: authHeaders });
      toast.success("Item added — ready to count");
      // Jump to the tab the new item landed in, so it isn't hidden behind the currently open tab.
      const landedArea = isNew ? addItemArea : selectableCatalogItems.find((it) => it.id === addItemChoice)?.storageArea;
      if (landedArea) setActiveTab(landedArea);
      setAddingItem(false);
      setAddItemChoice(NEW_ITEM_VALUE);
      setAddItemNewName("");
      await fetchData();
      onItemsChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add item");
    } finally {
      setAddingItemBusy(false);
    }
  };

  const handleConfirmBeginningBalance = async (itemId) => {
    setConfirmingBeginningFor(itemId);
    try {
      await axios.post(`${API}/inventory/items/${itemId}/confirm-beginning-balance`, {}, { headers: authHeaders });
      toast.success("Beginning Balance confirmed — this carries forward automatically from now on");
      await fetchData();
      onItemsChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm Beginning Balance");
    } finally {
      setConfirmingBeginningFor(null);
    }
  };

  const SUBCATEGORY_LABELS = [
    ["infant", "Infant"], ["youngToddler", "Young Toddler"],
    ["olderToddler", "Older Toddler"], ["preK", "PreK"],
  ];

  const renderTodayMealLine = (mealLabel, mealData) => {
    if (!mealData) return null;
    const parts = [];
    if (mealData.fullTime?.eligible) {
      SUBCATEGORY_LABELS.forEach(([key, label]) => parts.push(`${label} ${mealData.fullTime[key] ?? 0}`));
    }
    if (mealData.schoolAge?.eligible) parts.push(`School Age ${mealData.schoolAge.stillIn ?? 0}`);
    if (mealData.partTime?.eligible) parts.push(`Part Time ${mealData.partTime.stillIn ?? 0}`);
    if (mealData.afterSchool?.eligible) parts.push(`After School ${mealData.afterSchool.stillIn ?? 0}`);
    return (
      <p className="text-sm text-slate-700" data-testid={`today-meal-counts-${mealLabel.toLowerCase()}`}>
        <span className="font-semibold">{mealLabel}:</span>{" "}
        {parts.length > 0 ? parts.join(" · ") : "No eligible categories"} · <span className="font-semibold">Total {mealData.total ?? 0}</span>
      </p>
    );
  };

  if (loading) {
    return <p className="text-sm text-slate-400 py-6 text-center">Loading this month's items...</p>;
  }

  const items = data?.items || [];
  const locked = !!data?.approved;

  const formatLocalDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div data-testid="take-inventory-panel" className="space-y-4">
      <p data-testid="this-months-menu-items-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        Shows only items linked to this month's menu. Need something new added to inventory? Use '+ Add Item' here — it's added to your full catalog and linked to this month's menu in one step.
      </p>
      {mealAverages && (
        <div data-testid="month-to-date-meal-averages" className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <span className="font-semibold">
            Month-to-Date Meal Averages{mealAverages.hasData ? ` — ${mealAverages.snapshotCount} day${mealAverages.snapshotCount !== 1 ? "s" : ""} recorded:` : ":"}
          </span>{" "}
          {mealAverages.hasData
            ? <>
                Breakfast {mealAverages.breakfast ?? "—"} · Lunch {mealAverages.lunch ?? "—"} · Snack {mealAverages.snack ?? "—"}
                <span className="block text-xs text-slate-500 mt-0.5">Used for purchase planning.</span>
              </>
            : "No snapshot recorded yet this month. Set up Meal Settings to start tracking."}
        </div>
      )}
      {mealToday && (
        <div data-testid="today-meal-counts" className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-semibold">
              {mealTodayViewDate === orgTodayDate ? "Today's Meal Counts" : "Meal Counts"}
              {mealToday.date ? ` — ${formatLocalDate(mealToday.date)}` : ""}
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs font-normal" data-testid="meal-today-date-picker-trigger">
                  <CalendarIcon className="w-3 h-3 mr-1 text-slate-400" /> {formatMDY(mealTodayViewDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={isoToDate(mealTodayViewDate)}
                  onSelect={(d) => d && handleMealTodayDateChange(toIsoDate(d))}
                  disabled={(d) => orgTodayDate && toIsoDate(d) > orgTodayDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          {mealToday.hasData ? (
            <>
              {renderTodayMealLine("Breakfast", mealToday.breakfast)}
              {renderTodayMealLine("Lunch", mealToday.lunch)}
              {renderTodayMealLine("Snack", mealToday.snack)}
            </>
          ) : (
            <p className="text-slate-500">
              {mealTodayViewDate === orgTodayDate ? "No snapshot recorded yet today." : "No snapshot was recorded for this day."}
            </p>
          )}
        </div>
      )}
      {locked && (
        <div data-testid="take-inventory-locked-banner" className="flex items-center gap-2 text-sm bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
          <Lock className="w-4 h-4" /> This month's Shopping List was approved by {data.approvedBy} on{" "}
          {formatMDY(data.approvedAt)} and is frozen — it won't change from here.
          You can still log the final physical count below (it becomes next month's starting balance).
        </div>
      )}
      <div className="flex flex-col gap-2">
          {!addingItem ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="add-item-to-count-button" onClick={() => setAddingItem(true)} className="self-start">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
              <span className="text-xs text-slate-400" data-testid="remove-item-note">To remove an item, go to Master Item List.</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Select value={addItemChoice} onValueChange={setAddItemChoice}>
                <SelectTrigger className="w-56" data-testid="add-item-choice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NEW_ITEM_VALUE}>+ Add a new item</SelectItem>
                  {selectableCatalogItems.map((it) => (
                    <SelectItem key={it.id} value={it.id}>{it.canonicalName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {addItemChoice === NEW_ITEM_VALUE && (
                <>
                  <Input data-testid="add-item-new-name-input" placeholder="Item name" value={addItemNewName}
                    onChange={(e) => setAddItemNewName(e.target.value)} className="w-48" />
                  <Select value={addItemArea} onValueChange={setAddItemArea}>
                    <SelectTrigger className="w-40" data-testid="add-item-storage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AREAS.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <UnitTypeSelect value={addItemUnit} onChange={setAddItemUnit} testId="add-item-unit-select" triggerClassName="w-28" />
                </>
              )}
              <Button size="sm" data-testid="confirm-add-item-to-count-button" disabled={addingItemBusy} onClick={handleAddItem}>
                {addingItemBusy ? "Adding…" : "Add to Count"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAddingItem(false); setAddItemNewName(""); setAddItemChoice(NEW_ITEM_VALUE); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList data-testid="storage-area-tabs">
            {AREAS.map((area) => {
              const count = items.filter((i) => i.storageArea === area.key).length;
              return (
                <TabsTrigger key={area.key} value={area.key} data-testid={`storage-tab-${area.key}`}>
                  {area.label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>
          {AREAS.map((area) => {
          const areaItems = items.filter((i) => i.storageArea === area.key);
          return (
            <TabsContent key={area.key} value={area.key} className="mt-4">
              {areaItems.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">
                  No confirmed items for this month in {area.label} yet — confirm items on the "Match to Menu" step, or use "Add Item" above.
                </p>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-left px-3 py-2">
                          Package Size
                          <span className="block text-[10px] font-normal text-slate-400">Auto-updated from Master Item List — use Edit Item there to change</span>
                        </th>
                        <th className="text-left px-3 py-2">
                          Package Type
                          <span className="block text-[10px] font-normal text-slate-400">Auto-updated from Master Item List — use Edit Item there to change</span>
                        </th>
                        <th className="text-left px-3 py-2">
                          Beginning Balance
                          <span className="block text-[10px] font-normal text-slate-400">Auto</span>
                        </th>
                        <th className="text-left px-3 py-2">
                          Recorded Available
                          <span className="block text-[10px] font-normal text-slate-400">Auto</span>
                        </th>
                        <th className="text-left px-3 py-2">
                          Input Current Amount
                          <span className="block text-[10px] font-normal text-slate-500">Manual — required</span>
                        </th>
                        <th className="text-left px-3 py-2">
                          Quantity to Buy
                          <span className="block text-[10px] font-normal text-slate-500">Will be prefilled based on current stock and meal demand — can be adjusted</span>
                        </th>
                        <th className="text-left px-3 py-2">Notes</th>
                        <th className="text-left px-3 py-2">Save</th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaItems.map((item) => {
                        const beginning = data.beginningBalances?.[item.id];
                        const suggestion = data.suggestions?.[item.id];
                        const draft = drafts[item.id] || {};
                        const savedCount = data.counts?.[item.id];
                        const isFractional = item.unitType === "bag";
                        // Skip dishes that just repeat the item's own name (a plain 1:1 match) —
                        // only worth showing when it tells you something new, e.g. a shared/split ingredient.
                        const relevantDishes = (item.sourceDishes || []).filter(
                          (d) => d.toLowerCase() !== item.canonicalName.toLowerCase()
                        );
                        return (
                          <tr key={item.id} data-testid={`count-row-${item.id}`} className="border-t border-slate-100 align-top">
                            <td className="px-3 py-2 font-medium text-slate-800">
                              {item.canonicalName}
                              {relevantDishes.length > 0 && (
                                <p className="text-xs font-normal text-slate-400 mt-0.5" data-testid={`take-inventory-used-in-${item.id}`}>
                                  Used in: {relevantDishes.join(", ")}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{item.packageSize || "—"}</td>
                            <td className="px-3 py-2 text-slate-600" data-testid={`package-type-${item.id}`}>
                              {item.unitType ? item.unitType.charAt(0).toUpperCase() + item.unitType.slice(1) : "—"}
                            </td>
                            <td className="px-3 py-2 text-slate-500 text-xs" data-testid={`beginning-balance-${item.id}`}>
                              {!beginning ? (
                                <span data-testid={`needs-initial-inventory-${item.id}`} className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  Needs Initial Inventory
                                </span>
                              ) : beginning.confirmed ? (
                                `${beginning.amount} ${pluralUnit(beginning.unitType, beginning.amount)} (from ${MONTHS[beginning.month - 1]} ${beginning.year})`
                              ) : (
                                <div className="space-y-1">
                                  <span>
                                    Last recorded: {beginning.amount} {pluralUnit(beginning.unitType, beginning.amount)} ({MONTHS[beginning.month - 1]} {beginning.year})
                                  </span>
                                  <Button size="sm" variant="outline" className="h-6 text-xs block" data-testid={`confirm-beginning-balance-${item.id}`}
                                    disabled={confirmingBeginningFor === item.id}
                                    onClick={() => handleConfirmBeginningBalance(item.id)}>
                                    Use this as Beginning Balance
                                  </Button>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-600 text-xs" data-testid={`recorded-available-${item.id}`}>
                              {suggestion?.recordedAvailableBalance != null
                                ? `${suggestion.recordedAvailableBalance} ${pluralUnit(item.unitType, suggestion.recordedAvailableBalance)}`
                                : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              {isFractional && (
                                <div className="flex gap-1 mb-1">
                                  {PILLS.map((p) => (
                                    <button
                                      key={p.value}
                                      type="button"
                                      data-testid={`pill-${item.id}-${p.value}`}
                                      onClick={() => updateDraft(item.id, "currentAmount", String(p.value))}
                                      className={`text-xs px-2 py-1 rounded-md border ${
                                        String(draft.currentAmount) === String(p.value)
                                          ? "border-[#2F6DB5] bg-[#2F6DB5]/10 text-[#2F6DB5]"
                                          : "border-slate-200 text-slate-500 hover:border-slate-300"
                                      }`}
                                    >
                                      {p.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <Input type="number" step={isFractional ? "0.25" : "1"} min="0" className="h-8 w-20"
                                data-testid={`current-amount-input-${item.id}`}
                                value={draft.currentAmount ?? ""}
                                onChange={(e) => updateDraft(item.id, "currentAmount", e.target.value)} />
                            </td>
                            <td className="px-3 py-2">
                              <Input type="number" step="1" min="0" className="h-8 w-20"
                                data-testid={`quantity-to-buy-input-${item.id}`}
                                value={draft.quantityToBuy ?? ""}
                                onChange={(e) => updateDraft(item.id, "quantityToBuy", e.target.value)} />
                              {!savedCount && suggestion?.suggestedToBuy != null && (
                                <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`suggested-to-buy-hint-${item.id}`}>
                                  Suggested: {suggestion.suggestedToBuy} — edit freely before saving
                                </p>
                              )}
                              {!savedCount && suggestion?.missingInfoReason && (
                                <p className="text-[11px] text-amber-600 mt-0.5" data-testid={`missing-info-hint-${item.id}`}>
                                  {suggestion.missingInfoReason}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <Input className="h-8 w-32" placeholder="Optional"
                                data-testid={`notes-input-${item.id}`}
                                value={draft.notes ?? ""}
                                onChange={(e) => updateDraft(item.id, "notes", e.target.value)} />
                              {savedCount?.countedBy && (
                                <p className="text-xs text-slate-400 mt-1">
                                  {savedCount.countedBy}, {formatMDY(savedCount.countedAt)}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2">
                                <Button size="sm" className="h-8" data-testid={`save-count-${item.id}`}
                                  disabled={savingId === item.id}
                                  onClick={() => handleSave(item)}>
                                  Save
                                </Button>
                              </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          );
        })}
        </Tabs>
        {isOwner && (
          <div className="shrink-0">
            {addingArea ? (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                <Input data-testid="new-storage-area-input" placeholder='e.g. "Basement Freezer"'
                  value={newAreaLabel} onChange={(e) => setNewAreaLabel(e.target.value)} className="h-8 w-48" autoFocus />
                <Button size="sm" className="h-8" data-testid="confirm-add-storage-area-button" onClick={handleAddArea}>Save</Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setAddingArea(false); setNewAreaLabel(""); }}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" data-testid="add-storage-area-button" onClick={() => setAddingArea(true)}>
                <Plus className="w-4 h-4 mr-1" /> Add Area
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TakeInventoryPanel;
